/**
 * TimetablePage for viewing and managing tournament timetable configuration.
 * 
 * Features:
 * - View timetable for each tournament year
 * - Copy timetable to a new year
 * - Export/import backup JSON files
 */

import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import CopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import EditIcon from '@mui/icons-material/Edit';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { leagueRoundsApi, tournamentsApi } from '@/api/client';
import type { TournamentListItem, Tournament, LeagueRound } from '@/types';

// League display names and colors
const LEAGUE_CONFIG: Record<string, { name: string; color: string }> = {
  yle: { name: 'Yleinen', color: '#2196f3' },
  pro: { name: 'PRO', color: '#9c27b0' },
  frm: { name: 'Firma', color: '#ff9800' },
};

const TIME_SLOT_PATTERN = /^\d{2}:\d{2}$/;

// Format date for display
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('fi-FI', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// Group rounds by league
function groupRoundsByLeague(rounds: LeagueRound[]): Map<string, LeagueRound[]> {
  const groups = new Map<string, LeagueRound[]>();
  for (const round of rounds) {
    const existing = groups.get(round.league) || [];
    existing.push(round);
    groups.set(round.league, existing);
  }
  // Sort rounds within each league
  for (const [, leagueRounds] of groups) {
    leagueRounds.sort((a, b) => a.round_number - b.round_number);
  }
  return groups;
}

interface CopyDialogProps {
  open: boolean;
  sourceYear: number;
  onClose: () => void;
  onCopy: (targetYear: number, targetDate: string) => void;
  isLoading: boolean;
}

function CopyDialog({ open, sourceYear, onClose, onCopy, isLoading }: CopyDialogProps) {
  const [targetYear, setTargetYear] = useState(sourceYear + 1);
  const [targetDate, setTargetDate] = useState('');

  const handleSubmit = () => {
    if (targetYear && targetDate) {
      onCopy(targetYear, targetDate);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Copy Tournament to New Year</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Copy the timetable configuration from {sourceYear} to a new year.
        </Typography>
        <TextField
          label="Target Year"
          type="number"
          value={targetYear}
          onChange={(e) => setTargetYear(parseInt(e.target.value, 10))}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label="Tournament Date"
          type="date"
          value={targetDate}
          onChange={(e) => setTargetDate(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!targetYear || !targetDate || isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : <CopyIcon />}
        >
          Copy
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function TimetablePage() {
  const queryClient = useQueryClient();

  // State
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRound, setEditRound] = useState<LeagueRound | null>(null);
  const [editRoundName, setEditRoundName] = useState('');
  const [editCourts, setEditCourts] = useState('');
  const [editTimeSlots, setEditTimeSlots] = useState('');
  const [editError, setEditError] = useState('');

  // Fetch tournament list
  const {
    data: tournaments = [],
    isLoading: loadingList,
    error: listError,
  } = useQuery<TournamentListItem[]>({
    queryKey: ['tournaments'],
    queryFn: () => tournamentsApi.list(),
  });

  // Derive effective selected year - use first tournament if none selected
  const effectiveYear = selectedYear ?? (tournaments.length > 0 ? tournaments[0].year : null);

  // Fetch selected tournament details
  const {
    data: tournament,
    isLoading: loadingDetail,
  } = useQuery<Tournament>({
    queryKey: ['tournaments', effectiveYear],
    queryFn: () => tournamentsApi.get(effectiveYear!),
    enabled: effectiveYear !== null,
  });

  // Copy mutation
  const copyMutation = useMutation({
    mutationFn: ({ targetYear, targetDate }: { targetYear: number; targetDate: string }) =>
      tournamentsApi.copy(effectiveYear!, { target_year: targetYear, target_date: targetDate }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tournaments'] });
      setCopyDialogOpen(false);
      setSnackbar({
        open: true,
        message: `Copied to ${data.year} (${data.rounds_copied} rounds)`,
        severity: 'success',
      });
      setSelectedYear(data.year);
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to copy tournament',
        severity: 'error',
      });
    },
  });

  const handleOpenEditDialog = (round: LeagueRound) => {
    setEditRound(round);
    setEditRoundName(round.round_name ?? '');
    setEditCourts(JSON.stringify(round.courts, null, 2));
    setEditTimeSlots(JSON.stringify(round.time_slots, null, 2));
    setEditError('');
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditRound(null);
    setEditError('');
  };

  const updateRoundMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<LeagueRound> }) =>
      leagueRoundsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tournaments', effectiveYear] });
      setSnackbar({
        open: true,
        message: 'Round updated',
        severity: 'success',
      });
      handleCloseEditDialog();
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to update round',
        severity: 'error',
      });
    },
  });

  const handleSaveEditDialog = () => {
    if (!editRound) return;
    setEditError('');

    let parsedCourts: unknown;
    let parsedTimeSlots: unknown;

    try {
      parsedCourts = JSON.parse(editCourts);
    } catch {
      setEditError('Courts must be a valid JSON array of numbers.');
      return;
    }

    if (
      !Array.isArray(parsedCourts) ||
      !parsedCourts.every((value) => Number.isInteger(value))
    ) {
      setEditError('Courts must be a JSON array of integers.');
      return;
    }

    try {
      parsedTimeSlots = JSON.parse(editTimeSlots);
    } catch {
      setEditError('Time slots must be a valid JSON array of [start, end] pairs.');
      return;
    }

    if (
      !Array.isArray(parsedTimeSlots) ||
      !parsedTimeSlots.every(
        (slot) =>
          Array.isArray(slot) &&
          slot.length === 2 &&
          slot.every((value) => typeof value === 'string' && TIME_SLOT_PATTERN.test(value))
      )
    ) {
      setEditError('Time slots must be [["HH:MM", "HH:MM"], ...].');
      return;
    }

    updateRoundMutation.mutate({
      id: editRound.id,
      data: {
        round_name: editRoundName.trim(),
        courts: parsedCourts as number[],
        time_slots: parsedTimeSlots as Array<[string, string]>,
      },
    });
  };

  // Handle export
  const handleExport = async () => {
    if (!effectiveYear) return;
    try {
      const blob = await tournamentsApi.export(effectiveYear);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tournament_${effectiveYear}_backup.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSnackbar({
        open: true,
        message: 'Backup downloaded',
        severity: 'success',
      });
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to export backup',
        severity: 'error',
      });
    }
  };

  // Handle import
  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      await tournamentsApi.import({ data, overwrite: false });
      queryClient.invalidateQueries({ queryKey: ['tournaments'] });
      setSnackbar({
        open: true,
        message: 'Tournament imported successfully',
        severity: 'success',
      });
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to import backup',
        severity: 'error',
      });
    }
    // Reset file input
    event.target.value = '';
  };

  // Group rounds by league for display
  const roundsByLeague = useMemo(() => {
    if (!tournament) return new Map();
    return groupRoundsByLeague(tournament.rounds);
  }, [tournament]);

  if (loadingList) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (listError) {
    return (
      <Alert severity="error">Failed to load tournaments</Alert>
    );
  }

  if (tournaments.length === 0) {
    return (
      <Box>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Tournament Timetable
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          No tournaments configured. Import a backup to get started.
        </Alert>
        <Button
          variant="contained"
          component="label"
          startIcon={<UploadIcon />}
        >
          Import Backup
          <input
            type="file"
            hidden
            accept=".json"
            onChange={handleImport}
          />
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Tournament Timetable
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage tournament schedule configuration.
          </Typography>
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<CopyIcon />}
            onClick={() => setCopyDialogOpen(true)}
            disabled={!effectiveYear}
          >
            Copy to Year
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={!effectiveYear}
          >
            Save Backup
          </Button>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
          >
            Load Backup
            <input
              type="file"
              hidden
              accept=".json"
              onChange={handleImport}
            />
          </Button>
        </Box>
      </Box>

      {/* Year tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={effectiveYear}
          onChange={(_, year) => setSelectedYear(year)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tournaments.map((t) => (
            <Tab
              key={t.year}
              value={t.year}
              label={t.year}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tournament details */}
      {loadingDetail ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : tournament ? (
        <>
          {/* Tournament info */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {tournament.year} Tournament
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {formatDate(tournament.date)}
            </Typography>
            <Chip label={`${tournament.round_count} rounds`} variant="outlined" size="small" />
          </Paper>

          {/* Rounds by league */}
          {['yle', 'pro', 'frm'].map((league) => {
            const rounds = roundsByLeague.get(league) || [];
            if (rounds.length === 0) return null;
            const config = LEAGUE_CONFIG[league];

            return (
              <Paper key={league} sx={{ mb: 3 }}>
                <Box
                  sx={{
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: config.color,
                    }}
                  />
                  <Typography variant="h6">{config.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {rounds.length} rounds
                  </Typography>
                </Box>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Round</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Courts</TableCell>
                        <TableCell>Time Slots</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rounds.map((round: LeagueRound) => (
                        <TableRow key={round.id}>
                          <TableCell>
                            <Typography fontWeight={600}>
                              {round.round_number === 0 ? 'Bronze' : `Round ${round.round_number}`}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {round.round_name || '-'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {round.courts.length > 8
                                ? `${round.courts.slice(0, 4).join(', ')} ... (${round.courts.length} courts)`
                                : round.courts.join(', ')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {round.time_slots.length} slot{round.time_slots.length !== 1 ? 's' : ''}
                              {round.time_slots.length > 0 && (
                                <Typography
                                  component="span"
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ ml: 1 }}
                                >
                                  ({round.time_slots[0]?.[0]} - {round.time_slots[round.time_slots.length - 1]?.[1]})
                                </Typography>
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEditDialog(round)}
                              aria-label="Edit round"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            );
          })}
        </>
      ) : null}

      {/* Edit round dialog */}
      {editRound && (
        <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            Edit {editRound.league.toUpperCase()} Round {editRound.round_number}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Update courts and time slots for this round. Use JSON arrays.
            </Typography>
            {editError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {editError}
              </Alert>
            )}
            <TextField
              label="Round Name"
              value={editRoundName}
              onChange={(e) => setEditRoundName(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Courts (JSON array)"
              value={editCourts}
              onChange={(e) => setEditCourts(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              sx={{ mb: 2 }}
              placeholder="[28, 29, 30]"
            />
            <TextField
              label="Time Slots (JSON array)"
              value={editTimeSlots}
              onChange={(e) => setEditTimeSlots(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              placeholder='[["18:00", "18:30"]]'
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>Cancel</Button>
            <Button
              onClick={handleSaveEditDialog}
              variant="contained"
              disabled={updateRoundMutation.isPending}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Copy dialog */}
      {effectiveYear && (
        <CopyDialog
          open={copyDialogOpen}
          sourceYear={effectiveYear}
          onClose={() => setCopyDialogOpen(false)}
          onCopy={(targetYear, targetDate) =>
            copyMutation.mutate({ targetYear, targetDate })
          }
          isLoading={copyMutation.isPending}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

